import { Component } from 'react';
import HoldingLayout from './HoldingLayout';

const FEATURES = [
  {
    title: 'AI-Powered Generation',
    description: 'Generate professional, formal Arabic letters in seconds using artificial intelligence. Just pick a type and provide the key details — Khitab handles the structure, tone, and formatting.',
    icon: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="16" cy="16" r="12" /><path d="M12 16h8M16 12v8" opacity="0.5" /><circle cx="16" cy="16" r="4" />
      </svg>
    ),
  },
  {
    title: 'Arabic-First Design',
    description: 'Built from the ground up for Arabic. Right-to-left layout, proper Arabic typography, and culturally appropriate letter formats that meet Saudi organizational standards.',
    icon: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="4" width="24" height="24" rx="3" /><line x1="20" y1="10" x2="12" y2="10" /><line x1="22" y1="16" x2="10" y2="16" /><line x1="18" y1="22" x2="14" y2="22" />
      </svg>
    ),
  },
  {
    title: 'Transfer Letters',
    titleAr: 'خطاب نقل',
    description: 'Request a transfer from one department to another within the organization. Formally structured with proper chain-of-command language.',
    icon: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 16h24M20 10l6 6-6 6" /><path d="M28 16H4M12 22l-6-6 6-6" opacity="0.3" />
      </svg>
    ),
  },
  {
    title: 'Introduction Letters',
    titleAr: 'خطاب تعريف',
    description: 'Generate employee introduction letters directed at external parties. Professional and formal tone appropriate for official correspondence.',
    icon: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 4H20L26 10V28H6V4Z" /><polyline points="20 4 20 10 26 10" /><line x1="10" y1="16" x2="22" y2="16" /><line x1="10" y1="20" x2="18" y2="20" />
      </svg>
    ),
  },
  {
    title: 'Leave Requests',
    titleAr: 'طلب إجازة',
    description: 'Create formal leave requests addressed to the direct manager. Includes proper formatting for date ranges, leave type, and justification.',
    icon: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="6" width="24" height="22" rx="2" /><line x1="4" y1="12" x2="28" y2="12" /><line x1="10" y1="4" x2="10" y2="8" /><line x1="22" y1="4" x2="22" y2="8" /><circle cx="16" cy="20" r="3" opacity="0.4" />
      </svg>
    ),
  },
  {
    title: 'Internal Memos & More',
    titleAr: 'مذكرة داخلية',
    description: 'Official memos for internal communication between departments, assignment letters, appreciation letters, circulars, and complaint letters — all generated with the right structure.',
    icon: (
      <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="4" width="10" height="10" rx="2" /><rect x="18" y="4" width="10" height="10" rx="2" /><rect x="4" y="18" width="10" height="10" rx="2" /><rect x="18" y="18" width="10" height="10" rx="2" opacity="0.4" />
      </svg>
    ),
  },
];

const LETTER_TYPES = [
  { name: 'خطاب نقل', nameEn: 'Transfer Letter', desc: 'Department transfer requests' },
  { name: 'خطاب تعريف', nameEn: 'Introduction Letter', desc: 'Employee introductions' },
  { name: 'طلب إجازة', nameEn: 'Leave Request', desc: 'Formal leave applications' },
  { name: 'مذكرة داخلية', nameEn: 'Internal Memo', desc: 'Inter-department communication' },
  { name: 'خطاب تكليف', nameEn: 'Assignment Letter', desc: 'Task & responsibility assignments' },
  { name: 'خطاب شكر وتقدير', nameEn: 'Appreciation Letter', desc: 'Employee recognition' },
  { name: 'خطاب تعميم', nameEn: 'Circular', desc: 'Organization-wide announcements' },
  { name: 'خطاب شكوى', nameEn: 'Complaint Letter', desc: 'Formal complaints & objections' },
];

class ProductKhitab extends Component {
  render() {
    return (
      <HoldingLayout>
        {/* Hero */}
        <section className="h-page-hero">
          <div className="h-hero-bg-pattern" aria-hidden="true" />
          <div className="h-section-inner">
            <a href="/" className="h-back-link">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13 8H3M3 8L7 4M3 8L7 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              Back to Home
            </a>
            <div className="h-page-hero-content">
              <span className="h-section-tag">Product</span>
              <h1 className="h-page-hero-title">Khitab</h1>
              <p className="h-page-hero-arabic">خطاب — مولّد الخطابات الرسمية بالذكاء الاصطناعي</p>
              <p className="h-page-hero-desc">
                AI-powered formal letter generator for Arabic. Create professional
                official correspondence — transfer letters, leave requests, internal
                memos, appreciation letters, and more — in seconds.
              </p>
              <div className="h-page-hero-actions">
                <a href="https://khitab.app" target="_blank" rel="noopener noreferrer" className="h-btn-primary">
                  Visit Khitab
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </a>
                <span className="h-page-hero-status">
                  <span className="h-status-dot" />
                  Live
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Letter Types Grid */}
        <section className="h-page-features">
          <div className="h-section-inner">
            <div className="h-section-header">
              <span className="h-section-tag">Letter Types</span>
              <h2 className="h-section-title">8 letter types, one click each</h2>
              <p className="h-section-subtitle">
                Pick a type, fill in the details, and Khitab generates a professionally formatted Arabic letter ready to send.
              </p>
            </div>
            <div className="h-letter-types-grid">
              {LETTER_TYPES.map((lt, i) => (
                <div key={i} className="h-letter-type-card">
                  <span className="h-letter-type-ar">{lt.name}</span>
                  <span className="h-letter-type-en">{lt.nameEn}</span>
                  <span className="h-letter-type-desc">{lt.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="h-page-screenshots">
          <div className="h-section-inner">
            <div className="h-section-header">
              <span className="h-section-tag">Features</span>
              <h2 className="h-section-title">How Khitab works</h2>
              <p className="h-section-subtitle">
                Powered by AI, designed for the way Saudi organizations actually communicate.
              </p>
            </div>
            <div className="h-features-grid">
              {FEATURES.map((feature, i) => (
                <div key={i} className="h-feature-card">
                  <div className="h-feature-icon">{feature.icon}</div>
                  <h3 className="h-feature-title">{feature.title}</h3>
                  {feature.titleAr && <span className="h-feature-title-ar">{feature.titleAr}</span>}
                  <p className="h-feature-desc">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="h-page-cta">
          <div className="h-section-inner">
            <div className="h-cta-box">
              <h2 className="h-cta-title">Generate your first letter in seconds</h2>
              <p className="h-cta-desc">
                No templates to manage, no formatting to worry about. Just tell Khitab what you need.
              </p>
              <a href="https://khitab.app" target="_blank" rel="noopener noreferrer" className="h-btn-primary h-btn-lg">
                Try Khitab
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </a>
            </div>
          </div>
        </section>
      </HoldingLayout>
    );
  }
}

export default ProductKhitab;
